/**
 * Language Settings Page (Story 11.2)
 * Allows users to select their preferred language via radio buttons.
 */
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Card } from "../../components/molecules/Card";
import { Button } from "../../components/atoms/Button";
import { profileService } from "../../services/profileService";
import { useAuthStore } from "../../store/authStore";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../../i18n";
import i18n from "../../i18n";
import { getApiErrorMessage } from "../../utils/apiError";

const LOCALE_NATIVE_NAMES: Record<SupportedLocale, string> = {
  en: "English",
  fr: "Français",
  ar: "العربية",
};

export function Language() {
  const { t } = useTranslation("settings");
  const { user, setUser } = useAuthStore();

  const rawLang = i18n.language?.slice(0, 2) as SupportedLocale;
  const initialLocale: SupportedLocale = SUPPORTED_LOCALES.includes(rawLang) ? rawLang : "en";

  const [selected, setSelected] = useState<SupportedLocale>(initialLocale);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount to prevent state update on unmounted component
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await i18n.changeLanguage(selected);
      if (user) {
        const updated = await profileService.updateProfile({ locale: selected });
        setUser({ ...user, locale: updated.locale ?? selected });
      }
      const nativeName = LOCALE_NATIVE_NAMES[selected];
      const msg = t("language_changed_to", { language: nativeName });
      setSuccessMessage(msg);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save language preference"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      {/* Back link */}
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 mb-6"
      >
        <ArrowLeftIcon className="w-4 h-4 rtl-flip" />
        {t("title", "Settings")}
      </Link>

      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
        {t("language_title", "Language")}
      </h1>
      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
        {t("language_description", "Set your preferred language for the application.")}
      </p>

      <Card className="p-6">
        <fieldset>
          <legend className="sr-only">{t("language_title", "Language")}</legend>
          <div className="space-y-2">
            {(SUPPORTED_LOCALES as readonly SupportedLocale[]).map((locale) => (
              <label
                key={locale}
                className="flex items-center gap-3 min-h-[44px] px-3 py-2 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
              >
                <input
                  type="radio"
                  name="language"
                  value={locale}
                  checked={selected === locale}
                  onChange={() => setSelected(locale)}
                  className="min-h-[20px] min-w-[20px] text-primary-600 focus:ring-primary-500"
                />
                <span className="text-neutral-900 dark:text-neutral-100 text-sm font-medium">
                  {LOCALE_NATIVE_NAMES[locale]}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {/* Feedback messages */}
        {error && (
          <p className="mt-4 text-sm text-error-600 dark:text-error-400">{error}</p>
        )}

        <div className="mt-6">
          <Button onClick={handleSave} disabled={saving} variant="primary" size="md">
            {saving ? "..." : t("language_save", "Save Changes")}
          </Button>
        </div>
      </Card>

      {/* Toast notification */}
      {successMessage && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-4 end-4 z-50 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg"
        >
          {successMessage}
        </div>
      )}
    </div>
  );
}
